import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import {
  Container,
  FormGroup,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import { difference } from 'lodash';
import { makeSelectClients } from '../../selectors/clients';
import ConfirmationDialog from '../ConfirmationDialog';
import { getClient, updateClient } from '../../actions/clients';
import {
  createBusinessLine,
  deleteBusinessLine,
  hideDeleteBusinessLineError,
} from '../../actions/businessLines';

const useStyles = makeStyles(theme => ({
  defaultLobInput: {
    minWidth: 200,
  },
  lobHalves: {
    minWidth: '50%',
    display: 'inline-block',
  },
  lobHalvesOffset: {
    position: 'relative',
    top: '8px',
  },
  wrapper: {
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    borderBottom: '2px solid #DFDFDF',
    padding: theme.spacing(3),
  },
  grid: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
  },
}));

const ClientBusinessLinesConfigure = props => {
  const {
    selectedClient,
    onGetClient,
    onCreateBusinessLine,
    onDeleteBusinessLine,
    onHideDeleteBusinessLineError,
    deleteBusinessLineError,
  } = props;
  const classes = useStyles();

  const [medicareEnabled, setMedicareEnabled] = useState(
    (selectedClient.businessLines ?? []).find(({ name }) => name === 'medicare')
  );
  const [medicaidEnabled, setMedicaidEnabled] = useState(
    (selectedClient.businessLines ?? []).find(({ name }) => name === 'medicaid')
  );
  const [medicaidStates, setMedicaidStates] = useState(
    (selectedClient.businessLines ?? [])
      .filter(({ name }) => name === 'medicaid')
      .map(({ state }) => state)
  );
  const [deleteLobDialogOpen, setDeleteLobDialogOpen] = useState(false);

  const [deleteBusinessLineErrorDialogOpen, setDeleteBusinessLineErrorDialogOpen] = useState(
    deleteBusinessLineError !== undefined
  );

  const [currentEvent, setCurrentEvent] = useState({});

  useEffect(() => {
    setMedicareEnabled(
      !!(selectedClient.businessLines ?? []).find(({ name }) => name === 'medicare')
    );
    setMedicaidEnabled(
      !!(selectedClient.businessLines ?? []).find(({ name }) => name === 'medicaid')
    );
    setMedicaidStates(
      (selectedClient.businessLines ?? [])
        .filter(({ name }) => name === 'medicaid')
        .map(({ state }) => state)
    );
    setDeleteBusinessLineErrorDialogOpen(deleteBusinessLineError !== undefined);
  }, [
    selectedClient,
    deleteBusinessLineError,
    setMedicareEnabled,
    setMedicaidEnabled,
    setMedicaidStates,
  ]);

  const deleteLob = async () => {
    await onDeleteBusinessLine(
      selectedClient.businessLines.find(({ name }) => name === 'medicare').id
    );
    setMedicareEnabled(currentEvent.target?.checked);
    onGetClient(selectedClient.id);
    setDeleteLobDialogOpen(false);
  };

  const hideDeleteBusinessLineErrorFunc = async () => {
    await onHideDeleteBusinessLineError();
    setDeleteBusinessLineErrorDialogOpen(false);
  };

  const comp = (
    <Grid className={classes.grid} maxwidth="lg">
      <Paper>
        <Grid className={classes.lobHalves} xs={6}>
          <Grid container spacing={0} direction="row" wrap="nowrap">
            <Grid item xs={8}>
              <Typography className={classes.sectionTitle} variant="h5" component="h3">
                Lines of Business
              </Typography>
            </Grid>
          </Grid>
          <Grid container maxwidth="lg" className={classes.grid}>
            <Container className={classes.wrapper} maxwidth="lg">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      data-testid="medicareSwitch"
                      checked={medicareEnabled || false}
                      onChange={async event => {
                        if (event.target.checked) {
                          await onCreateBusinessLine(selectedClient.id, 'medicare');
                          setMedicareEnabled(event.target?.checked);
                          onGetClient(selectedClient.id);
                        } else {
                          setCurrentEvent(event);
                          setDeleteLobDialogOpen(true);
                        }
                      }}
                    />
                  }
                  label="Medicare"
                />
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={medicaidEnabled || false}
                        onChange={async event => {
                          if (!event.target.checked) {
                            await Promise.all(
                              selectedClient.businessLines
                                .filter(({ name }) => name === 'medicaid')
                                .map(({ id }) => onDeleteBusinessLine(id))
                            );
                            setMedicaidStates([]);
                            onGetClient(selectedClient.id);
                          }
                          setMedicaidEnabled(event.target?.checked);
                        }}
                      />
                    }
                    label="Medicaid"
                  />
                  <Autocomplete
                    disabled={!medicaidEnabled}
                    required={medicaidEnabled}
                    multiple
                    value={medicaidStates}
                    onChange={async ({ target }, update) => {
                      const selected = target.innerHTML;
                      const states = update.filter(state => state.length === 2);
                      if (!medicaidStates.includes(selected) && selected.length === 2) {
                        await onCreateBusinessLine(selectedClient.id, 'medicaid', selected);
                      } else {
                        const [removed] = difference(medicaidStates, update);
                        if (removed)
                          await Promise.all(
                            selectedClient.businessLines
                              .filter(({ state }) => state === removed)
                              .map(thing => {
                                const { id } = thing;
                                return onDeleteBusinessLine(id);
                              })
                          );
                      }
                      if (!states.length) setMedicaidEnabled(false);
                      setMedicaidStates(states);
                      onGetClient(selectedClient.id);
                    }}
                    options={[
                      'AL',
                      'AK',
                      'AZ',
                      'AR',
                      'CA',
                      'CO',
                      'CT',
                      'DE',
                      'DC',
                      'FL',
                      'GA',
                      'HI',
                      'ID',
                      'IL',
                      'IN',
                      'IA',
                      'KS',
                      'KY',
                      'LA',
                      'ME',
                      'MD',
                      'MA',
                      'MI',
                      'MN',
                      'MS',
                      'MO',
                      'MT',
                      'NE',
                      'NV',
                      'NH',
                      'NJ',
                      'NM',
                      'NY',
                      'NC',
                      'ND',
                      'OH',
                      'OK',
                      'OR',
                      'PA',
                      'RI',
                      'SC',
                      'SD',
                      'TN',
                      'TX',
                      'UT',
                      'VT',
                      'VA',
                      'WA',
                      'WV',
                      'WI',
                      'WY',
                      'PR',
                    ]}
                    renderInput={params => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <TextField {...params} variant="standard" label="States" />
                    )}
                  />
                </FormGroup>
              </FormGroup>
              <ConfirmationDialog
                title="Line of business disable"
                description="Are you sure you want to disable the line of business?"
                primaryButtonText="Yes"
                onPrimaryButtonClick={() => {
                  deleteLob();
                }}
                open={deleteLobDialogOpen}
                onSecondaryButtonClick={() => setDeleteLobDialogOpen(false)}
                secondaryButtonText="Cancel"
              />
              <Dialog
                open={deleteBusinessLineErrorDialogOpen}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">Error deleting business line</DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description" /> {deleteBusinessLineError}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => hideDeleteBusinessLineErrorFunc()} autoFocus>
                    OK
                  </Button>
                </DialogActions>
              </Dialog>
            </Container>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );

  return comp;
};

ClientBusinessLinesConfigure.defaultProps = {
  selectedClient: {},
  deleteBusinessLineError: undefined,
};

ClientBusinessLinesConfigure.propTypes = {
  selectedClient: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    billingAddressLine1: PropTypes.string,
    billingAddressLine2: PropTypes.string,
    billingAddressZip: PropTypes.string,
    billingAddressState: PropTypes.string,
    billingAddressCity: PropTypes.string,
    connection: PropTypes.string,
    defaultBusinessLineId: PropTypes.number,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        applicationId: PropTypes.number,
        clientId: PropTypes.number,
        deletedAt: PropTypes.string,
        auth0ClientId: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    businessLines: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        state: PropTypes.state,
        clientId: PropTypes.number,
      })
    ),
  }),
  onGetClient: PropTypes.func.isRequired,
  onUpdateClient: PropTypes.func.isRequired,
  onCreateBusinessLine: PropTypes.func.isRequired,
  onDeleteBusinessLine: PropTypes.func.isRequired,
  onHideDeleteBusinessLineError: PropTypes.func.isRequired,
  deleteBusinessLineError: PropTypes.string,
};

const mapStateToProps = state => {
  const { businessLines } = state;
  let businessLineError;
  if (businessLines !== undefined) {
    businessLineError = businessLines.deleteBusinessLineError;
  }
  return {
    clients: makeSelectClients(),
    deleteBusinessLineError: businessLineError,
  };
};

export default connect(mapStateToProps, {
  onGetClient: getClient,
  onUpdateClient: updateClient,
  onCreateBusinessLine: createBusinessLine,
  onDeleteBusinessLine: deleteBusinessLine,
  onHideDeleteBusinessLineError: hideDeleteBusinessLineError,
})(ClientBusinessLinesConfigure);
